import {
    Cpu,
    Globe,
    Code,
    Server
} from 'lucide-react';
import { companyLogos } from '../assets/logos/companyLogos';
import { schoolLogos } from '../assets/logos/schoolLogos';
import { projectLogos } from '../assets/logos/projectLogos';
import { certificationLogos } from '../assets/logos/certificationLogos';
import { hackathonLogos } from '../assets/logos/hackathonLogos';

// Helper function to generate UI Avatars URL
const getAvatar = (name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=d92323&color=fff&size=200&bold=true`;

export const skills = [
    { icon: Cpu, title: 'AI & Machine Learning', skills: ['PyTorch', 'TensorFlow', 'Scikit-learn', 'OpenCV', 'NLP', 'Computer Vision', 'Emotion Detection', 'LangChain', 'FAISS'] },
    { icon: Globe, title: 'Web Development', skills: ['React', 'TypeScript', 'Node.js', 'Express.js', 'Django (DRF)', 'PHP', 'Tailwind CSS'] },
    { icon: Code, title: 'Languages', skills: ['Python', 'Java', 'C/C++', 'JavaScript', 'SQL'] },
    { icon: Server, title: 'Infrastructure & Tools', skills: ['Git', 'Docker', 'Linux', 'Azure AD', 'VMware', 'PostgreSQL', 'MongoDB'] },
];

export const experiences = [
    {
        translationKey: 'sofrecom',
        role: 'Generative AI Engineer Intern',
        company: 'Sofrecom',
        location: 'Tunis, Tunisia · Hybrid',
        period: 'Jul 2026 - Present',
        description: [
            'Developing Generative AI solutions for telecom and enterprise applications.',
            'Working on LLM-based systems, prompt engineering, and AI pipeline architecture.',
        ],
        tags: ['Generative AI', 'LLMs', 'Telecom', 'AI Engineering'],
        logo: companyLogos['Sofrecom'],
        companyUrl: 'https://www.sofrecom.com',
        socialLinks: {
            website: 'https://www.sofrecom.com',
            linkedin: 'https://www.linkedin.com/company/sofrecom'
        }
    },
    {
        translationKey: 'phundit',
        role: 'Applied AI Engineer Intern',
        company: 'Phundit',
        location: 'Ghana · Remote',
        period: 'Oct 2025 - Feb 2026',
        description: [
            'Developed AI-driven solutions for fintech applications.',
            'Focused on scalable API architecture and system security.',
        ],
        tags: ['AI', 'Fintech', 'Remote'],
        logo: companyLogos['Phundit'],
        companyUrl: 'https://phundit.app',
        socialLinks: {
            linkedin: 'https://www.linkedin.com/company/phunditapp',
            website: 'https://phundit.app'
        }
    },
    {
        translationKey: 'atia',
        role: 'Teaching Assistant & Mentor',
        company: 'ATIA Club ESB',
        location: 'Ariana, Tunisia · Hybrid',
        period: 'Oct 2025 - Present',
        description: [
            'Mentoring students in AI and software development projects.',
            'Assisting in organizing workshops and technical sessions.',
        ],
        tags: ['Mentorship', 'Teaching', 'Community'],
        logo: companyLogos['ATIA Club ESB'],
        companyUrl: 'https://www.facebook.com/ATIA.club.esb',
        socialLinks: {
            facebook: 'https://www.facebook.com/profile.php?id=100089378468766&ref=_xav_ig_profile_page_web',
            linkedin: 'https://www.linkedin.com/company/atia-club-esb',
            instagram: 'https://www.instagram.com/atia_club_esb/',
        }
    },
    {
        translationKey: 'rfc',
        role: 'AI Intern',
        company: 'RFC',
        location: 'Ariana, Tunisia · Remote',
        period: 'Jun 2025 - Aug 2025',
        description: [
            'Developed a Django REST backend and React TypeScript frontend for an enterprise RAG platform.',
            'Integrated Azure AD & LDAP authentication with role-based access control and user management.',
            'Implemented multi-model LLM support (Ollama, Groq) with intelligent fallback mechanisms.',
            'Built document processing pipelines with FAISS vector search, query rewriting, and contextual filtering.',
            'Enhanced scalability and resilience with async processing, error handling, and performance optimization.',
            'Designed admin dashboards for system monitoring, user analytics, and RAG performance metrics.'
        ],
        tags: ['RAG', 'Django', 'React', 'Azure AD', 'FAISS', 'LLMs'],
        logo: companyLogos['RFC'],
        companyUrl: 'https://rfc.com.tn',
        socialLinks: {
            website: 'https://rfc.com.tn',
            linkedin: 'https://www.linkedin.com/company/rfc_2'
        }
    },
    {
        translationKey: 'ge',
        role: 'IT Infrastructure Intern',
        company: 'GE Digital',
        location: 'Radès, Tunisia · On-site',
        period: 'Jul 2023 - Aug 2023',
        description: [
            'Set up and managed VMs using VMware.',
            'Configured DNS servers and records.',
            'Defined firewall rules with FortiGate.',
            'Managed Group Policies (GPO) on Windows.',
            'Administered systems remotely via PowerShell.',
            'Studied and applied APM/OPM for turbine performance monitoring.'
        ],
        tags: ['VMware', 'Networking', 'PowerShell', 'FortiGate'],
        logo: companyLogos['GE Digital'],
        companyUrl: 'https://www.ge.com/digital',
        socialLinks: {
            website: 'https://www.ge.com/digital',
            linkedin: 'https://www.linkedin.com/company/ge-digital',
            twitter: 'https://twitter.com/GE_Digital'
        }
    }
];

export const education = [
    {
        translationKey: '3il',
        school: "3iL Ingénieurs",
        degree: "Engineer's degree, Computer Science & AI",
        period: "Sep 2026 - Present",
        grade: null,
        activities: null,
        logo: schoolLogos['3il'],
        website: 'https://www.3il-ingenieurs.fr',
        socialLinks: {
            website: 'https://www.3il-ingenieurs.fr',
            linkedin: 'https://www.linkedin.com/school/3il-ingenieurs'
        }
    },
    {
        translationKey: 'esprit',
        school: "Ecole Supérieure Privée d'Ingénierie et de Technologies - ESPRIT",
        degree: "Engineer's degree, Artificial Intelligence",
        period: "Sep 2022 - 2027",
        grade: "Grade: 4",
        activities: "Member @ IEEE CS & CIS ESPRIT SB, Member -> Project Developer -> TA & Mentor @ ATIA Club ESB",
        logo: schoolLogos.esprit,
        website: 'https://esprit.tn',
        socialLinks: {
            website: 'https://esprit.tn',
            linkedin: 'https://www.linkedin.com/school/esprit',
            facebook: 'https://www.facebook.com/Esprit.Tunis'
        }
    },
    {
        school: "Taher Sfar High School",
        degree: "High School Diploma with Honors, Mathematics",
        period: "Sep 2018 - Jun 2022",
        grade: null,
        activities: null,
        logo: schoolLogos.lts,
        website: null,
        socialLinks: null
    }
];

export const hackathons = [
    {
        translationKey: 'smart-campus-2026',
        name: "ESPRIT Smart Campus Challenge",
        organizer: "Cisco Club Esprit, ESPRIT, Yakdhane",
        date: "May 2026",
        location: "Ariana, Tunisia",
        achievement: "1st Place 🏆🥇",
        eventUrl: "",
        description: "Secured 1st Place at the ESPRIT Smart Campus Challenge by engineering an autonomous, AI-driven campus management system that transforms static university infrastructure into a living, adapting ecosystem. Delivered three pillars of real-world impact: Predictive Energy Optimization (LSTM model forecasting foot traffic to dynamically adjust network hardware power and cut electricity costs while minimizing EMF exposure), Proactive Physical Security (real-time computer vision to detect smoking in restricted zones and hardware-level biometric authentication for the admin command center), and Dynamic 3D Visualization (a real-time dashboard leveraging Generative AI to convert 2D images into interactive 3D spatial assets for facility managers).",
        tags: ["Smart Campus", "Green IT", "Artificial Intelligence", "IoT", "Sustainability", "Machine Learning", "Computer Vision", "LSTM", "Biometrics"],
        logo: hackathonLogos.esprit,
        teammates: [
            {
                name: "Omar Bouhaja",
                linkedin: "",
                profilePicture: getAvatar("Omar Bouhaja")
            },
            {
                name: "Slim Sassi",
                linkedin: "",
                profilePicture: getAvatar("Slim Sassi")
            },
            {
                name: "Salma Ben Hmida",
                linkedin: "",
                profilePicture: getAvatar("Salma Ben Hmida")
            }
        ],
        projectName: "Secure & Green",
        coverPhoto: "/assets/hackathons/smart-campus-2026/cover.jpg",
        photos: [
            {
                url: "/assets/hackathons/smart-campus-2026/smoking-detection.jpg"
            },
            {
                url: "/assets/hackathons/smart-campus-2026/2.jpg"
            },
            {
                url: "/assets/hackathons/smart-campus-2026/36a20fe1-e6ae-4a7d-8a5e-4cf877510988.jpg"
            },
            {
                url: "/assets/hackathons/smart-campus-2026/a9b23f14-f599-4b08-aef8-cdf8d6d7d2f7.jpg"
            }
        ]
    },
    {
        translationKey: 'cyberia-2026',
        name: "CYBERIA 2026 Hackathon",
        organizer: "ESPRIT, Smart Skills, RDI CyberSec Intelligence, TekArt, EPIK LEADERS",
        date: "Apr 2026",
        location: "Tunisia",
        achievement: "1st Place 🏆🥇",
        eventUrl: "",
        description: "Secured 1st Place at the CYBERIA 2026 Hackathon by creating Godzilla CTI—an AI-powered platform that moves a SOC from passive logging to active, predictive threat intelligence. The system features a GenAI HoneyPot that mimics vulnerable Linux environments, a Predictive Forecaster for attack volumes, a Live Blast Radius for tracking lateral movement, and biometric hardware integrated directly into the SIEM alert feed.",
        tags: ["Cybersecurity", "Artificial Intelligence", "Hackathon", "Machine Learning", "Threat Intelligence", "SOC", "LLM"],
        logo: hackathonLogos.esprit,
        teammates: [
            {
                name: "Omar Bouhaja",
                linkedin: "",
                profilePicture: getAvatar("Omar Bouhaja")
            },
            {
                name: "Abdelhak Abrougui",
                linkedin: "",
                profilePicture: getAvatar("Abdelhak Abrougui")
            },
            {
                name: "Hafedh Chaibi",
                linkedin: "",
                profilePicture: getAvatar("Hafedh Chaibi")
            }
        ],
        projectName: "Godzilla CTI",
        coverPhoto: "/assets/hackathons/cyberia-2026/cover.jpg",
        photos: [
            {
                url: "/assets/hackathons/cyberia-2026/1.jpg"
            },
            {
                url: "/assets/hackathons/cyberia-2026/2.jpg"
            },
            {
                url: "/assets/hackathons/cyberia-2026/WhatsApp Image 2026-04-26 at 3.23.45 PM.jpeg"
            }
        ]
    },
    {
        translationKey: 'entreprenup-2026',
        name: "Entrepren'up Hackathon",
        organizer: "ESPRIT, MedSirat, Siratify, Startup Village, Excella System",
        date: "Apr 2026",
        location: "Tunisia",
        achievement: "2nd Place 🥈",
        description: "Secured 2nd Place with HestIA, a PropTech platform transforming how properties are rented and bought. HestIA provides a map-first, Sims-inspired real estate experience with Instant 3D Property Tours, Lifestyle Simulation AI to predict roommate conflicts, Predictive Costing, and a 24/7 Procedural Assistant.",
        tags: ["PropTech", "Real Estate", "Artificial Intelligence", "Startups", "Investment", "Innovation", "Entrepreneurship"],
        logo: hackathonLogos.esprit,
        teammates: [
            {
                name: "Aziz Maatoug",
                linkedin: "",
                profilePicture: getAvatar("Aziz Maatoug")
            },
            {
                name: "Emna Nkhili",
                linkedin: "",
                profilePicture: getAvatar("Emna Nkhili")
            },
            {
                name: "Fedi Abassi",
                linkedin: "",
                profilePicture: getAvatar("Fedi Abassi")
            },
            {
                name: "Maha Mensi",
                linkedin: "",
                profilePicture: getAvatar("Maha Mensi")
            },
            {
                name: "Idriss Benmoussa",
                linkedin: "",
                profilePicture: getAvatar("Idriss Benmoussa")
            }
        ],
        projectName: "HestIA",
        coverPhoto: "/assets/hackathons/entreprenup-2026/cover.jpg",
        photos: [
            {
                url: "/assets/hackathons/entreprenup-2026/1.jpg"
            },
            {
                url: "/assets/hackathons/entreprenup-2026/2.jpg"
            },
            {
                url: "/assets/hackathons/entreprenup-2026/HQFK8278.JPEG"
            },
            {
                url: "/assets/hackathons/entreprenup-2026/IMG_2228 (1).JPG"
            },
            {
                url: "/assets/hackathons/entreprenup-2026/IMG_5784.jpg"
            },
            {
                url: "/assets/hackathons/entreprenup-2026/IMG_5825.jpg"
            },
            {
                url: "/assets/hackathons/entreprenup-2026/IMG_5991.jpg"
            }
        ]
    },

    {
        translationKey: 'agroera-2025',
        name: "AGROERA 2025 - 13th Edition JEnovate Challenge",
        organizer: "Institut National Agronomique de Tunisie (INAT Junior Entreprise)",
        date: "Dec 2025",
        location: "Tunis, Tunisia",
        achievement: "3rd Place 🥉",
        eventUrl: "https://www.facebook.com/events/2287714738343501",
        description: "Secured 3rd place at the JEnovate challenge with Agri3D, a multi-agent AI decision support system designed to shift agriculture from reactive disaster response to proactive prevention. Built a comprehensive risk analysis platform that examines three interconnected layers: Climate (forecasting droughts and environmental stress), Markets (analyzing price volatility and financial trends), and Geopolitics (tracking global supply shocks and conflicts). Developed a 2D farm simulator enabling farmers to test 'what-if' scenarios tailored to their specific risk profiles. The system provides actionable insights to help farmers predict, prepare, and prosper.",
        tags: ["Multi-Agent AI", "AgriTech", "Decision Support", "Risk Analysis", "Farm Simulation", "Climate Forecasting", "Market Analysis", "Sustainability"],
        logo: hackathonLogos.inat,
        teammates: [
            {
                name: "Med Aziz Maatoug",
                linkedin: "https://www.linkedin.com/in/mohamedazizmaatoug",
                profilePicture: getAvatar("Med Aziz Maatoug")
            },
            {
                name: "Fedi Abassi",
                linkedin: "https://www.linkedin.com/in/fedi-abassi",
                profilePicture: getAvatar("Fedi Abassi")
            }
        ],
        projectName: "Agri3D",
        coverPhoto: "/assets/hackathons/agroera-2025/cover.jpg",
        photos: [
            {
                url: "/assets/hackathons/agroera-2025/team-photo1.jpg"
            },
            {
                url: "/assets/hackathons/agroera-2025/team-pitching.jpg"
            },
            {
                url: "/assets/hackathons/agroera-2025/award-ceremony.jpg"
            },
            {
                url: "/assets/hackathons/agroera-2025/team-photo2.jpg"
            }
        ]
    },
    {
        translationKey: 'hack-nation-2025',
        name: "Hack-Nation Global AI Hackathon (3rd Edition)",
        organizer: "MIT Sloan AI Club",
        date: "Nov 8-9, 2025",
        location: "Global (Virtual)",
        achievement: "Hackathon Participant",
        eventUrl: "https://hack-nation.ai",
        description: "Built Praxis AI, a multi-agent system designed to accelerate R&D from code to commercialization. Developed a Research Paper Generator using a swarm of AI agents that transforms raw Jupyter Notebooks into fully structured and illustrated research papers, automatically generating new diagrams where visual gaps are detected. Created an Industry Navigator with a second team of agents that analyzes research papers to assess novelty against existing research and identifies concrete, high-value industrial applications. Leveraged LangChain/LangGraph for multi-agent architecture, FastAPI for the backend, and various Groq API's LLMs for generation and analysis.",
        tags: ["Multi-Agent AI", "LangChain", "FastAPI", "React", "Research", "R&D", "Tech Transfer"],
        logo: hackathonLogos.mitSloan,
        teammates: [
            {
                name: "Med Aziz Maatoug",
                linkedin: "https://www.linkedin.com/in/mohamedazizmaatoug",
                profilePicture: getAvatar("Med Aziz Maatoug")
            },
            {
                name: "Idriss Benmoussa",
                linkedin: "https://www.linkedin.com/in/idriss-benmoussa-a68ab4357/",
                profilePicture: getAvatar("Idriss Benmoussa")
            },
            {
                name: "Linda Sakouhi",
                linkedin: "https://www.linkedin.com/in/linda-sakouhi-1059b6333/",
                profilePicture: getAvatar("Linda Sakouhi")
            }
        ],
        projectName: "Praxis AI",
        coverPhoto: "/assets/hackathons/hack-nation-2025/1.jpg",
        photos: [
            {
                url: "/assets/hackathons/hack-nation-2025/praxis-team.jpg"
            },
            {
                url: "/assets/hackathons/hack-nation-2025/system-architecture.jpg"
            },
            {
                url: "/assets/hackathons/hack-nation-2025/demo-praxis.jpg"
            },
            {
                url: "/assets/hackathons/hack-nation-2025/mit-recognition.jpg"
            }
        ]
    },
    {
        translationKey: 'aixcyber-2025',
        name: "AIxCyber 2025 Hackathon",
        organizer: "Esprit School of Business & Engineering",
        date: "Oct 18-19, 2025",
        location: "Ariana, Tunisia",
        achievement: "Top 10 Finalists (out of 44 teams)",
        eventUrl: "https://www.facebook.com/events/1066896585313602",
        description: "Built a secure and intelligent FinTech ecosystem merging AI, Cybersecurity, and Blockchain in 12 hours. Features included offline payment simulation, ML-powered behavior tracking, YOLOv8-based asset management with NFT tokenization, stock market simulation, LSTM trends forecasting, global legal intelligence, and hybrid security & anomaly detection.",
        tags: ["FinTech", "AI", "Cybersecurity", "Blockchain", "LSTM", "YOLOv8", "NFT"],
        certificateUrl: "/assets/certificates/hackathons/Certificat - AIXCYBER.pdf",
        logo: hackathonLogos.esprit,
        teammates: [
            {
                name: "Med Aziz Maatoug",
                linkedin: "https://www.linkedin.com/in/mohamedazizmaatoug",
                profilePicture: getAvatar("Med Aziz Maatoug")
            },
            {
                name: "Med Anas Obba",
                linkedin: "https://www.linkedin.com/in/medanasobba",
                profilePicture: getAvatar("Med Anas Obba")
            },
            {
                name: "Fedi Abassi",
                linkedin: "https://www.linkedin.com/in/fedi-abassi",
                profilePicture: getAvatar("Fedi Abassi")
            },
            {
                name: "Ghassen EL ABED",
                linkedin: "https://www.linkedin.com/in/ghassenelabed/",
                profilePicture: getAvatar("Ghassen EL ABED")
            },
            {
                name: "Tesnime Ellabou",
                linkedin: "https://www.linkedin.com/in/tesnime-ellabou-3170981b8/",
                profilePicture: getAvatar("Tesnime Ellabou")
            }
        ],
        projectName: "InvestIA",

        coverPhoto: "/assets/hackathons/aixcyber-2025/1.jpg",
        photos: [
            {
                url: "/assets/hackathons/aixcyber-2025/2.jpg"
            },
            {
                url: "/assets/hackathons/aixcyber-2025/3.jpg"
            }
        ]
    },
    {
        translationKey: 'ains-2025',
        name: "AINS 2025 - Artificial Intelligence National Summit",
        organizer: "IEEE INSAT SB CS Chapter",
        date: "Jun 13-15, 2025",
        location: "Tunisia",
        achievement: "Hackathon Participant",
        description: "Participated in the Artificial Intelligence National Summit, a national event featuring keynote talks, workshops, data science competitions, and poster sessions. Collaborated with Samar Souissi and Med Anas Obba in the hackathon, building innovative AI solutions and connecting with brilliant minds in the field.",
        tags: ["AI", "Hackathon", "IEEE", "Innovation", "Teamwork"],
        certificateUrl: "/assets/certificates/hackathons/AINS 3.0.jpg",
        logo: hackathonLogos.ieee,
        teammates: [
            {
                name: "Samar Souissi",
                linkedin: "https://www.linkedin.com/in/samar-souissi-321b90308/",
                profilePicture: getAvatar("Samar Souissi")
            },
            {
                name: "Med Anas Obba",
                linkedin: "https://www.linkedin.com/in/medanasobba",
                profilePicture: getAvatar("Med Anas Obba")
            }
        ],
        coverPhoto: "/assets/hackathons/ains-2025/cover.jpg",
        photos: [
            {
                url: "/assets/hackathons/ains-2025/1.jpg"
            },
            {
                url: "/assets/hackathons/ains-2025/2.jpg"
            },
            {
                url: "/assets/hackathons/ains-2025/3.jpg"
            },
            {
                url: "/assets/hackathons/ains-2025/4.jpg"
            },
            {
                url: "/assets/hackathons/ains-2025/5.jpg"
            }
        ]
    },
    {
        translationKey: 'd-clickathon-2024',
        name: "D-Clickathon 2024",
        organizer: "Organisation Internationale de la Francophonie (OIF)",
        date: "Dec 7-8, 2024",
        location: "Espace El Maken- TIC-DCE, La Marsa",
        achievement: "Hackathon Participant",
        certificateUrl: "/assets/certificates/hackathons/D-Clickathon.jpg",
        description: "Participated in D-Clickathon, an international hackathon organized by the Organisation Internationale de la Francophonie (OIF) focused on creating gamified prototypes that strengthen professional and linguistic skills. The event brought together experts, civil society actors, entrepreneurs, academics, and young talents to explore opportunities in gaming and artificial intelligence while promoting the French language in professional sectors.",
        tags: ["Gaming", "AI", "French Language", "Gamification", "OIF", "EdTech"],
        logo: hackathonLogos.oif,
        teammates: [{
            name: "Fedi Abassi",
            linkedin: "https://www.linkedin.com/in/fedi-abassi",
            profilePicture: getAvatar("Fedi Abassi")
        }],
        projectName: "MediLearn",
        coverPhoto: "/assets/hackathons/d-clickathon-2024/1.jpg",
        photos: [
            {
                url: "/assets/hackathons/d-clickathon-2024/event-setup.jpg"
            },
            {
                url: "/assets/hackathons/d-clickathon-2024/workshop.jpg"
            }
        ]
    },
    {
        translationKey: 'ieeextreme-17',
        name: "IEEEXtreme 17.0 Programming Competition",
        organizer: "IEEE Computer Society",
        date: "Oct 2023",
        location: "Global",
        achievement: "Hackathon Participant",
        description: "24-hour global programming competition testing problem-solving skills, algorithms, and coding expertise across various domains. An intense but rewarding experience pushing technical boundaries.",
        tags: ["Programming", "Algorithms", "Problem Solving", "IEEE"],
        certificateUrl: "/assets/certificates/hackathons/Certif XTREME 17.0.pdf",
        logo: hackathonLogos.ieee,
        coverPhoto: "/assets/hackathons/ieeextreme-17/competition-setup.jpg",
        teammates: [
            {
                name: "Med Anas Obba",
                linkedin: "https://www.linkedin.com/in/medanasobba",
                profilePicture: getAvatar("Med Anas Obba")
            },
        ],
        photos: [
            {
                url: "/assets/hackathons/ieeextreme-17/competition-setup.jpg"
            },
            {
                url: "/assets/hackathons/ieeextreme-17/problem-solving.jpg"
            },
            {
                url: "/assets/hackathons/ieeextreme-17/participation-cert.jpg"
            }
        ]
    }
];

export const certifications = [
    {
        name: "Building Transformer-Based NLP Applications",
        issuer: "NVIDIA",
        date: "Mar 2026",
        id: "hDdG8XkTQC6XulUKIuzAnw",
        logo: certificationLogos.nvidia,
        verifyUrl: "https://learn.nvidia.com/certificates?id=hDdG8XkTQC6XulUKIuzAnw"
    },
    {
        name: "Designing Agentic Systems with LangChain",
        issuer: "DataCamp",
        date: "Oct 2025",
        id: "43,538,572",
        logo: certificationLogos.datacamp,
        verifyUrl: "https://www.datacamp.com/completed/statement-of-accomplishment/course/efcaa44b88d088e2daf36c9ce2a3497ca7c0b92e"
    },
    {
        name: "Understanding Microsoft Azure",
        issuer: "DataCamp",
        date: "Jul 2025",
        id: "41,508,518",
        logo: certificationLogos.datacamp,
        verifyUrl: "https://www.datacamp.com/completed/statement-of-accomplishment/course/591cb1e37aff161d12b4bb9580aff5419c43c470"
    },
    {
        name: "Understanding Cloud Computing",
        issuer: "DataCamp",
        date: "Jun 2025",
        id: "41,480,888",
        logo: certificationLogos.datacamp,
        verifyUrl: "https://www.datacamp.com/completed/statement-of-accomplishment/course/7ea8d64c181bc6d8efb805ec0fab63ae5ba2c81b"
    },
    {
        name: "Adding New Knowledge to LLMs",
        issuer: "NVIDIA",
        date: "Nov 2025",
        id: "QDlasdwYRdOKQco0jVztMg",
        logo: certificationLogos.nvidia,
        verifyUrl: "https://learn.nvidia.com/certificates?id=QDlasdwYRdOKQco0jVztMg"
    },
    {
        name: "Applications of AI for Predictive Maintenance",
        issuer: "NVIDIA",
        date: "Nov 2025",
        id: "y2GrziguRJuud54uToUikg",
        logo: certificationLogos.nvidia,
        verifyUrl: "https://learn.nvidia.com/certificates?id=y2GrziguRJuud54uToUikg"
    },
    {
        name: "Applications of AI for Anomaly Detection",
        issuer: "NVIDIA",
        date: "Nov 2025",
        id: "4q2iCnoASDO69HDXj5vNGg",
        logo: certificationLogos.nvidia,
        verifyUrl: "https://learn.nvidia.com/certificates?id=4q2iCnoASDO69HDXj5vNGg"
    },
    {
        name: "Building AI Agents with Multimodal Models",
        issuer: "NVIDIA",
        date: "Nov 2025",
        id: "Rns-mQ5sSnOokgVp7hXRZQ",
        logo: certificationLogos.nvidia,
        verifyUrl: "https://learn.nvidia.com/certificates?id=Rns-mQ5sSnOokgVp7hXRZQ"
    },
    {
        name: "TOEFL iBT (Score: 96/120)",
        issuer: "ETS",
        date: "May 2025",
        id: "3847 6052 5135 7617",
        logo: certificationLogos.ets,
        certificateUrl: "/assets/certificates/TOEFL_Score_Report.pdf",
        verifyUrl: "/assets/certificates/TOEFL_Score_Report.pdf"
    },
    {
        name: "Generative AI with Diffusion Models",
        issuer: "NVIDIA",
        date: "Jan 2025",
        id: "pFQsJDADTGq_n8riJvRg0g",
        logo: certificationLogos.nvidia,
        verifyUrl: "https://learn.nvidia.com/certificates?id=pFQsJDADTGq_n8riJvRg0g#"
    },
    {
        name: "Introduction to Transformer-Based NLP",
        issuer: "NVIDIA",
        date: "Jan 2025",
        id: "He2KfYSaRj28w6FgPx1-aA",
        logo: certificationLogos.nvidia,
        verifyUrl: "https://learn.nvidia.com/certificates?id=fCBI8CozTISs7N8nOvxFgg"
    },
    {
        name: "Fundamentals of Deep Learning",
        issuer: "NVIDIA",
        date: "Dec 2024",
        id: "1tO0Ys3ITkGJkXM3sgBKrQ",
        logo: certificationLogos.nvidia,
        verifyUrl: "https://learn.nvidia.com/certificates?id=g_YlArD1RhWSssqE5P2YUQ"
    },
    {
        name: "Data Cleaning and Preprocessing with pandas",
        issuer: "365 Data Science",
        date: "Nov 2024",
        id: "CC-0BD88DB1FF",
        logo: certificationLogos.dataScience365,
        verifyUrl: "https://learn.365datascience.com/c/daf25b8cc5"
    },
    {
        name: "Mathematics",
        issuer: "365 Data Science",
        date: "Nov 2024",
        id: "CC-17A05BDEAB",
        logo: certificationLogos.dataScience365,
        verifyUrl: "https://learn.365datascience.com/c/3298ca72d4"
    }
];

export const projects = [
    {
        translationKey: 'hestia',
        title: 'HestIA',
        description: 'Full-stack AI-powered apartment intelligence platform combining real-time environmental assessment, LLM-driven roommate compatibility simulation, and 3D room visualization — all in an interactive map-based interface.',
        tags: ['Django', 'React', 'TypeScript', 'LLMs', 'Three.js', 'YOLO-World', 'OpenStreetMap'],
        winner: true,
        logo: projectLogos.hestia,
        media: {
            type: 'mixed',
            items: [
                { type: 'gdrive', src: 'https://drive.google.com/file/d/1BTiyfJNS9CS2BVlDEG_Z7bjUtQyPaeHU/preview', title: 'HestIA Commercial' },
                { type: 'image', src: '/assets/projects/hestia/screenshot1.png', title: 'Screenshot 1' },
                { type: 'image', src: '/assets/projects/hestia/screenshot2.png', title: 'Screenshot 2' },
                { type: 'image', src: '/assets/projects/hestia/screenshot3.png', title: 'Screenshot 3' },
                { type: 'image', src: '/assets/projects/hestia/screenshot4.png', title: 'Screenshot 4' },
                { type: 'image', src: '/assets/projects/hestia/screenshot5.png', title: 'Screenshot 5' },
            ]
        },
        links: {
            github: 'https://github.com/TeraMSE/HestIA'
        }
    },

    {
        translationKey: 'savyor',
        title: 'SavyOR',
        description: 'AI-Powered Life Crisis Response System. Features real-time multimodal first aid assistant, ST-GNN hotspot prediction, and RL dispatch optimization.',
        tags: ['Python', 'TypeScript', 'PyTorch'],
        winner: false,
        logo: projectLogos.savyor,
        media: {
            type: 'mixed',
            items: [
                { type: 'video', src: '/assets/projects/savyor/3d_demo.mp4', title: '3D Simulation Demo' },
                { type: 'video', src: '/assets/projects/savyor/mobile_demo.mp4', title: 'Mobile App Demo' },
                { type: 'image', src: '/assets/projects/savyor/3d_pipeline.jpg', title: '3D Pipeline Architecture' },
                { type: 'image', src: '/assets/projects/savyor/mobile_pipeline.jpg', title: 'Mobile Pipeline Architecture' },
            ]
        },
        links: {}
    },
    {
        translationKey: 'aigis',
        title: 'AIGIS',
        description: 'AI-powered online safety browser extension. Detects violence, deepfakes, and hate speech in real-time using deep learning (InceptionV3, RoBERTa). Features behavioral analysis, epilepsy protection, and a parental dashboard.',
        tags: ['Django', 'RAG', 'Deep Learning', 'Computer Vision'],
        winner: true,
        logo: projectLogos.aigis,
        media: {
            type: 'mixed', // 'video', 'images', 'presentation', 'mixed'
            items: [
                { type: 'image', src: '/assets/projects/aigis/cover.png', title: 'Cover' },
                { type: 'youtube', src: 'https://youtu.be/SYdXexWjmkI', title: 'Commercial Video' },
                { type: 'youtube', src: 'https://youtu.be/i8WabWizGDU', title: 'AIGIS Pitch Deck' },
                { type: 'image', src: '/assets/projects/aigis/brainrot detection.jpg', title: 'Brainrot Detection' },
                { type: 'image', src: '/assets/projects/aigis/deepfake detection.jpg', title: 'Deepfake Detection' },
                { type: 'image', src: '/assets/projects/aigis/nudity filtering.jpg', title: 'Nudity Filtering' },
                { type: 'image', src: '/assets/projects/aigis/scrolling.gif', title: 'Pattern Analysis' },]
        },
        links: {
            github: 'https://github.com/SynapsysESPRIT/AIGIS',
        }
    },
    {
        translationKey: 'praxis-ai',
        title: '[Hack-Nation 3.0] Praxis AI',
        description: 'Multi-agent research accelerator platform. Automates converting Jupyter notebooks into formal research papers and industrial application reports using LangChain and LLM swarms.',
        tags: ['LangChain', 'Neo4j', 'React', 'FastAPI'],
        winner: false,
        logo: projectLogos.praxisAi,
        media: {
            type: 'mixed',
            items: [
                { type: 'image', src: '/assets/projects/praxis ai/stats.jpg', title: 'Stats' },
                { type: 'image', src: '/assets/projects/praxis ai/pipeline1.jpg', title: 'Paper Generator' },
                { type: 'image', src: '/assets/projects/praxis ai/pipeline2.jpg', title: 'Novelty Assessor' }

            ]
        },
        links: {
            github: 'https://github.com/shemIA-Hack-Nation/praxis-ai'
        }
    },
    {
        translationKey: 'investia',
        title: '[AIxCyber2025] InvestIA',
        description: 'Secure intelligent Fintech ecosystem involving AI personas for investment advice, CV-powered asset tokenization, and hybrid anomaly detection for fraud prevention.',
        tags: ['LangChain', 'Blockchain', 'Cybersecurity', 'YOLOv8'],
        winner: true,
        logo: projectLogos.aixCyber,
        media: {
            type: 'mixed',
            items: [
                { type: 'image', src: '/assets/projects/investia/investia.jpg', title: 'InvestIA' },
                { type: 'image', src: '/assets/projects/investia/whatwebuilt.jpg', title: 'What We Built' },
                { type: 'image', src: '/assets/projects/investia/asset.jpg', title: 'Asset Tokenization with CV' }]
        },
        links: {
            github: 'https://github.com/TeamMerlin-HackESB/InvestIA'
        }
    },
    {
        translationKey: 'medilearn',
        title: 'MediLearn',
        description: 'AI-powered 2D educational game for learning French. Uses Computer Vision for drawing recognition and NLP for pronunciation feedback within a Unity environment.',
        tags: ['NLP', 'Computer Vision', 'Unity', 'LLMs'],
        winner: false,
        logo: projectLogos.mediLearn,
        media: {
            type: 'mixed',
            items: [
                // TODO: Replace with actual YouTube URLs once uploaded
                { type: 'youtube', src: 'https://youtu.be/MswDDO8WbMY', title: 'The Grand Enchanted Eye of Knowledge Demo' },
                { type: 'youtube', src: 'https://youtu.be/Cp0gwvZ4raA', title: 'Ye Olde Drawing Scroll Demo' },
                { type: 'youtube', src: 'https://youtu.be/cpa4DHk1IIk', title: 'Text-to-Speech Demo' },
                { type: 'youtube', src: 'https://youtu.be/M6JuZVZEXj0', title: 'Speech-to-Text Demo' },

            ]
        },

    },
    {
        translationKey: 'dishboard',
        title: 'Dishboard',
        description: 'AI Recipe generation app using Spoonacular API and OpenCV for pantry ingredient detection. Includes a research summarizer for nutrition articles.',
        tags: ['Django', 'OpenCV', 'Spoonacular API'],
        winner: false,
        logo: projectLogos.dishboard,
        media: {
            type: 'mixed',
            items: [
                // TODO: Replace with actual YouTube URL once uploaded
                { type: 'youtube', src: 'https://youtu.be/eb4_yQHm30Y', title: 'Dishboard Demo' },
                { type: 'image', src: '/assets/projects/dishboard/home.jpg', title: 'Home Page' },
                { type: 'image', src: '/assets/projects/dishboard/ingredient.jpg', title: 'Ingredient Detection' },
                { type: 'image', src: '/assets/projects/dishboard/pantry.jpg', title: 'Add Ingredient to Pantry' },
                { type: 'image', src: '/assets/projects/dishboard/blog.jpg', title: 'Blog' },
                { type: 'image', src: '/assets/projects/dishboard/summarizer.jpg', title: 'AI Article Summarizer' },
            ]
        },
        links: {
            github: 'https://github.com/SynapsysESPRIT/Dishboard'
        }
    },
    {
        translationKey: 'time-conquest',
        title: 'Time Conquest',
        description: '2D C-based video game using SDL. Recognized among top 16 projects at Bal des Projets. Features custom Arduino gamepad integration.',
        tags: ['C', 'SDL', 'Arduino', 'Game Dev'],
        winner: true,
        logo: projectLogos.timeConquest,
        media: {
            type: 'mixed',
            items: [

            ]
        }
    }
];